import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Upload, X, Save, Eye, Loader2, Tag, Calendar, User } from 'lucide-react';
import toast from 'react-hot-toast';
import { slideService } from './slideService';

interface SlideFormData {
  title: string;
  full_description: string;
  order: number;
  button_text: string;
  button_link: string;
  impact_areas: string;
  date?: string;
  author?: string;
  category?: string;
  image?: File;
}

const SlideForm: React.FC<{ isEdit?: boolean }> = ({ isEdit = false }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [uploading] = useState(false);
  const [existingImage, setExistingImage] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue
  } = useForm<SlideFormData>({
    defaultValues: {
      title: '',
      full_description: '',
      order: 1,
      button_text: 'Read More',
      button_link: '',
      impact_areas: '',
      date: '',
      author: '',
      category: ''
    }
  });

  // Fetch existing data for edit
  useEffect(() => {
    if (isEdit && id) {
      fetchSlide();
    }
  }, [id, isEdit]);

  const fetchSlide = async () => {
    try {
      setLoading(true);
      const response = await slideService.getById(parseInt(id!));
      
      if (response.success) {
        const data = response.data;
        reset({
          title: data.title,
          full_description: data.full_description,
          order: data.order,
          button_text: data.button_text || 'Read More',
          button_link: data.button_link || '',
          impact_areas: data.impact_areas?.join(', ') || '',
          date: data.date || '',
          author: data.author || '',
          category: data.category || ''
        });
        
        if (data.image_url) {
          setImagePreview(data.image_url);
          setExistingImage(data.image_url);
        }
      } else {
        toast.error('Failed to fetch slide');
        navigate('/admin/home/slides');
      }
    } catch (error: any) {
      console.error('Fetch error:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch slide');
      navigate('/admin/home/slides');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please upload a valid image (JPEG, PNG, WebP)');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }

      setValue('image', file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const removeImage = () => {
    setValue('image', undefined);
    setImagePreview(existingImage || '');
  };

    const onSubmit = async (data: SlideFormData) => {
      console.log('Form data submitted:', data);
      
      try {
        setLoading(true);
        
        // Ensure CSRF token is fresh
        await slideService.initializeCsrf();
        
        const formData = new FormData();
        formData.append('title', data.title);
        formData.append('full_description', data.full_description);
        formData.append('order', data.order.toString());
        formData.append('button_text', data.button_text || 'Read More');
        formData.append('button_link', data.button_link || '');
        
        if (data.impact_areas) {
          formData.append('impact_areas', data.impact_areas);
        }
        
        if (data.date) {
          formData.append('date', data.date);
        }
        
        if (data.author) {
          formData.append('author', data.author);
        }
        
        if (data.category) {
          formData.append('category', data.category);
        }
        
        if (data.image) {
          formData.append('image', data.image);
        }
        
        // Log FormData entries for debugging
        console.log('FormData entries:');
        for (let [key, value] of formData.entries()) {
          console.log(`${key}:`, value instanceof File ? `File: ${value.name}` : value);
        }
        
        let response;
        if (isEdit && id) {
          console.log('Updating slide ID:', id);
          response = await slideService.update(parseInt(id), formData);
        } else {
          console.log('Creating new slide');
          response = await slideService.create(formData);
        }
        
        console.log('API Response:', response);
        
        if (response.success) {
          toast.success(`Slide ${isEdit ? 'updated' : 'created'} successfully`);
          navigate('/admin/home/slides');
        } else {
          toast.error(response.message || 'Operation failed');
        }
      } catch (error: any) {
        console.error('Submit error details:', error);
        
        if (error.response?.status === 419) {
          toast.error('CSRF token expired. Please refresh the page and try again.');
          // Try to refresh CSRF token
          await slideService.initializeCsrf();
        } else if (error.response?.data?.errors) {
          const errors = error.response.data.errors;
          Object.keys(errors).forEach(key => {
            toast.error(`${key}: ${errors[key].join(', ')}`);
          });
        } else {
          toast.error(error.response?.data?.message || 'Something went wrong');
        }
      } finally {
        setLoading(false);
      }
    };

  if (loading && isEdit) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/admin/home/slides"
          className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-4"
        >
          <ArrowLeft size={18} className="mr-2" />
          Back to Slides
        </Link>
        <h1 className="text-3xl font-bold text-gray-800">
          {isEdit ? 'Edit Slide' : 'Create New Slide'}
        </h1>
        <p className="text-gray-600 mt-2">
          {isEdit ? 'Update the slide details' : 'Add a new slide to display on the homepage slider'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Form Fields */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Basic Information</h2>
              <div className="space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    {...register('title', { required: 'Title is required' })}
                    className={`w-full px-4 py-3 rounded-lg border ${
                      errors.title ? 'border-red-300' : 'border-gray-300'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    placeholder="Enter slide title"
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                  )}
                </div>

                {/* Full Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Description *
                  </label>
                  <textarea
                    {...register('full_description', { required: 'Full description is required' })}
                    rows={6}
                    className={`w-full px-4 py-3 rounded-lg border ${
                      errors.full_description ? 'border-red-300' : 'border-gray-300'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    placeholder="Enter detailed description of the slide/project"
                  />
                  {errors.full_description && (
                    <p className="mt-1 text-sm text-red-600">{errors.full_description.message}</p>
                  )}
                  <p className="mt-1 text-sm text-gray-500">
                    {watch('full_description')?.length || 0}/1000+ characters
                  </p>
                </div>

                {/* Order */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Display Order
                  </label>
                  <input
                    type="number"
                    min="0"
                    {...register('order', { min: 0, valueAsNumber: true })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Lower numbers appear first in the slider
                  </p>
                </div>
              </div>
            </div>

            {/* Button Settings */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Button Settings</h2>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Button Text
                    </label>
                    <input
                      type="text"
                      {...register('button_text')}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Read More"
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Default is "Read More"
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Button Link
                    </label>
                    <input
                      type="text"
                      {...register('button_link')}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="sustainable-solutions"
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      URL slug or full path
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Additional Information</h2>
              <div className="space-y-4">
                {/* Impact Areas */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Impact Areas
                  </label>
                  <input
                    type="text"
                    {...register('impact_areas')}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Education, Health, Empowerment"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Enter impact areas separated by commas
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type="text"
                        {...register('date')}
                        className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="January 15, 2024"
                      />
                    </div>
                  </div>

                  {/* Author */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Author
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type="text"
                        {...register('author')}
                        className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="NORA Team"
                      />
                    </div>
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <div className="relative">
                      <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type="text"
                        {...register('category')}
                        className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Education Action"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Image Upload & Actions */}
          <div className="space-y-6">
            {/* Image Upload */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Slide Image *</h2>
              
               {imagePreview ? (
              <div className="space-y-4">
                <div className="relative">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-64 object-cover rounded-lg"
                    onError={(e) => {
                      console.error('Image failed to load:', imagePreview);
                      (e.target as HTMLImageElement).src = 'https://placehold.co/1920x800/2563eb/white?text=Image+Error';
                    }}
                  />
                  {!existingImage && (
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => document.getElementById('image-upload')?.click()}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    Change Image
                  </button>
                </div>
              </div>
            ) : (
              <div
                onClick={() => document.getElementById('image-upload')?.click()}
                className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
              >
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <Upload className="text-gray-400" size={24} />
                </div>
                <p className="text-gray-600 mb-2">
                  <span className="text-blue-600 font-medium">Click to upload</span> or drag and drop
                </p>
                <p className="text-sm text-gray-500">
                  PNG, JPG, WebP up to 5MB
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Recommended: 1920×800px
                </p>
              </div>
            )}
              
              <input
                id="image-upload"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              
              {uploading && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                    <span className="text-blue-700">Uploading image...</span>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Actions</h2>
              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      <span>{isEdit ? 'Update Slide' : 'Create Slide'}</span>
                    </>
                  )}
                </button>
                
                <button
                  type="button"
                  onClick={() => navigate('/admin/home/slides')}
                  className="w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                
                {isEdit && existingImage && (
                  <a
                    href={existingImage}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2"
                  >
                    <Eye size={18} />
                    <span>View Image</span>
                  </a>
                )}
              </div>
            </div>

            {/* Tips */}
            <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-2">Tips</h3>
              <ul className="space-y-2 text-sm text-blue-700">
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 mr-2"></div>
                  Use high-quality images (1920×800px recommended)
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 mr-2"></div>
                  Keep titles concise and impactful
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 mr-2"></div>
                  Button text will default to "Read More" if left empty
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 mr-2"></div>
                  Impact areas: enter comma-separated values
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 mr-2"></div>
                  Lower order numbers appear first
                </li>
              </ul>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SlideForm;